import React, {useState} from "react";
import {Formik, Form, Field, ErrorMessage} from "formik";
import * as Yup from "yup";
import "./FormPage.css";
import {Typography} from "@mui/material";
import {showSnackbar} from "../../../../components/SnackBar/SnackBar";
import {ApiDetails} from "../../../../Constants";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import CryptoJS from "crypto-js";
import { handle401Unauthorized, handle403Forbidden } from '../../../../utils/authErrorHandler';
import { getAuthToken } from '../../../../services/api/client';


export default function FormComponent(props) {
    const [loading, setLoading] = useState(false);

    const originalText = props.originalDetails;

    const tenantDetails = {
        first_name: props.tenatDetails.first_name,
        last_name: props.tenatDetails.last_name,
        email_address: props.tenatDetails.email_address,
        phone_number: props.tenatDetails.phone_number,
        secondary_phone_number: props.tenatDetails.secondary_phone_number,
        rent: props.tenatDetails.rent,
        national_id: props.tenatDetails.national_id,

        next_of_kin_relationship: props.tenatDetails.next_of_kin_relationship,
        next_of_kin_first_name: props.tenatDetails.next_of_kin_first_name,
        next_of_kin_last_name: props.tenatDetails.next_of_kin_last_name,
        next_of_kin_national_id: props.tenatDetails.next_of_kin_national_id,
        next_of_kin_contacts: props.tenatDetails.next_of_kin_contacts,
    };

    const tenantValidationSchema = Yup.object({
        first_name: Yup.string().required("First name is required"),
        last_name: Yup.string().required("Last name is required"),
        email_address: Yup.string().email("Invalid email address"),
        phone_number: Yup.string()
            .required("Phone number is required")
            .matches(
                /^(\+?254|0)?[17]\d{8}$/,
                "Enter a valid Kenyan phone number"
            ),
        secondary_phone_number: Yup.string()
            .matches(
                /^(\+?254|0)?[17]\d{8}$/,
                "Enter a valid Kenyan phone number"
            ),
        rent: Yup.number()
            .required("Rent is required")
            .min(1, "Rent must be at least 1")
            .typeError("Rent must be a valid number"),
        // Removed national_id validation - now optional
        national_id: Yup.string()
            .nullable()
            .transform((value, originalValue) => {
                // Transform empty string to null for validation
                return originalValue === "" ? null : value;
            })
            .matches(
                /^\d{5,15}$/,
                "National ID must be 7-8 digits when provided"
            )
            .typeError("National ID must be a valid number when provided"),
        // Also make next of kin national ID optional
        next_of_kin_national_id: Yup.number()
            .nullable()
            .transform((value, originalValue) => {
                return originalValue === "" ? null : value;
            })
            .min(5, "Next of kin National ID must be at least 5 digits when provided")
            .typeError("Next of kin National ID must be a valid number when provided"),
    });

    const handleSubmit = (values) => {
        if (values.first_name === "") {
            showSnackbar({
                title: "Error!",
                message: "Enter a valid first name.",
                type: "error",
                position: {vertical: "top", horizontal: "right"},
            });
            return;
        }
        if (values.last_name === "") {
            showSnackbar({
                title: "Error!",
                message: "Enter a valid last name.",
                type: "error",
                position: {vertical: "top", horizontal: "right"},
            });
            return;
        }
        if (values.rent === "") {
            showSnackbar({
                title: "Error!",
                message: "Enter a valid rent.",
                type: "error",
                position: {vertical: "top", horizontal: "right"},
            });
            return;
        }
        if (values.phone_number === "") {
            showSnackbar({
                title: "Error!",
                message: "Enter a valid phone number.",
                type: "error",
                position: {vertical: "top", horizontal: "right"},
            });
            return;
        }

        // START LOADING
        setLoading(true);

        // Prepare the data, setting national IDs to 0 if empty or null
        const submitData = {
            tenant_id: props.tenatDetails.tenant_id,
            first_name: values.first_name,
            last_name: values.last_name,
            national_id: values.national_id || 0, // Default to 0 if empty
            email_address: values.email_address,
            phone_number: values.phone_number,
            occupation_or_profession: values.occupation_or_profession,
            next_of_kin_relationship: values.next_of_kin_relationship,
            next_of_kin_first_name: values.next_of_kin_first_name,
            next_of_kin_last_name: values.next_of_kin_last_name,
            next_of_kin_national_id: values.next_of_kin_national_id || 0, // Default to 0 if empty
            next_of_kin_contacts: values.next_of_kin_contacts,
            rent: values.rent,
            secondary_phone_number: values.secondary_phone_number,
        };

        //Make API call
        fetch(ApiDetails + "pegasus/visionary/tenant/update/tenant", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": getAuthToken() || ""
            },
            body: JSON.stringify(submitData),
        })
            .then(async (response) => {
                setLoading(false); // STOP LOADING
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    showSnackbar({
                        title: "Success!",
                        message: "Tenant details updated successfully.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed updating tenant details.",
                        type: "error",
                        position: {vertical: "top", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                setLoading(false); // STOP LOADING ON ERROR
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong while processing your request.",
                    type: "error",
                    position: {vertical: "top", horizontal: "right"},
                });
            });

        console.log("Form Data Submitted:", submitData);
    };

    return (
        <div className="container">
            <Formik
                initialValues={tenantDetails}
                validationSchema={tenantValidationSchema}
                onSubmit={handleSubmit}
            >
                {({isSubmitting}) => (
                    <Form className="form">
                        {/* First Name and Last Name */}
                        <div className="form-row">
                            <div className="form-group">
                                <Typography variant="subtitle1">First Name:</Typography>
                                <Field
                                    type="text"
                                    id="first_name"
                                    name="first_name"
                                    placeholder="Enter First Name"
                                    className="light-blue"
                                />
                                <ErrorMessage
                                    name="first_name"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                            <div className="form-group">
                                <Typography variant="subtitle1">Last Name:</Typography>
                                <Field
                                    type="text"
                                    id="last_name"
                                    name="last_name"
                                    placeholder="Enter Last Name"
                                    className="light-blue"
                                />
                                <ErrorMessage
                                    name="last_name"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                        </div>

                        {/* Email and National ID */}
                        <div className="form-row">
                            <div className="form-group">
                                <Typography variant="subtitle1">Email:</Typography>
                                <Field
                                    type="email"
                                    id="email_address"
                                    name="email_address"
                                    placeholder="Enter Email"
                                    className="light-blue"
                                />
                                <ErrorMessage
                                    name="email_address"
                                    component="div"
                                    className="error-message"
                                />
                            </div>

                            <div className="form-group">
                                <Typography variant="subtitle1">
                                    National ID <span style={{color: '#666', fontSize: '0.9em'}}>(Optional)</span>:
                                </Typography>
                                <Field
                                    type="number"
                                    id="national_id"
                                    name="national_id"
                                    placeholder="Enter National ID (Optional)"
                                    className="light-blue"
                                />
                                <ErrorMessage
                                    name="national_id"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                        </div>

                        {/* Phone Numbers */}
                        <div className="form-row">
                            <div className="form-group">
                                <Typography variant="subtitle1">Phone Number:</Typography>
                                <Field
                                    type="text"
                                    id="phone_number"
                                    name="phone_number"
                                    placeholder="Enter Phone Number"
                                    className="light-blue"
                                />
                                <ErrorMessage
                                    name="phone_number"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                            <div className="form-group">
                                <Typography variant="subtitle1">Secondary Phone Number:</Typography>
                                <Field
                                    type="text"
                                    id="secondary_phone_number"
                                    name="secondary_phone_number"
                                    placeholder="Enter Secondary Phone Number"
                                    className="light-blue"
                                />
                                <ErrorMessage
                                    name="secondary_phone_number"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                        </div>

                        {/* Rent */}
                        <div className="form-row">
                            <div className="form-group">
                                <Typography variant="subtitle1">Rent:</Typography>
                                <Field
                                    type="text"
                                    id="rent"
                                    name="rent"
                                    placeholder="Enter rent"
                                    className="light-blue"
                                />
                                <ErrorMessage
                                    name="rent"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                        </div>

                        <div style={{width: "fit-content"}}>
                            <h3 className="section-title">Next of Kin Details</h3>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <Typography variant="subtitle1">First Name:</Typography>
                                <Field
                                    type="text"
                                    id="next_of_kin_first_name"
                                    name="next_of_kin_first_name"
                                    placeholder="Enter First Name"
                                    className="light-blue"
                                />
                                <ErrorMessage
                                    name="next_of_kin_first_name"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                            <div className="form-group">
                                <Typography variant="subtitle1">Last Name:</Typography>
                                <Field
                                    type="text"
                                    id="next_of_kin_last_name"
                                    name="next_of_kin_last_name"
                                    placeholder="Enter Last Name"
                                    className="light-blue"
                                />
                                <ErrorMessage
                                    name="next_of_kin_last_name"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <Typography variant="subtitle1">Relationship:</Typography>
                                <Field
                                    type="text"
                                    id="next_of_kin_relationship"
                                    name="next_of_kin_relationship"
                                    placeholder="Enter Relationship"
                                    className="light-blue"
                                />
                                <ErrorMessage
                                    name="next_of_kin_relationship"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                            <div className="form-group">
                                <Typography variant="subtitle1">Phone Number:</Typography>
                                <Field
                                    type="text"
                                    id="next_of_kin_contacts"
                                    name="next_of_kin_contacts"
                                    placeholder="Enter Phone Number"
                                    className="light-blue"
                                />
                                <ErrorMessage
                                    name="next_of_kin_contacts"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <Typography variant="subtitle1">
                                    National ID <span style={{color: '#666', fontSize: '0.9em'}}>(Optional)</span>:
                                </Typography>
                                <Field
                                    type="number"
                                    id="next_of_kin_national_id"
                                    name="next_of_kin_national_id"
                                    placeholder="Enter National ID (Optional)"
                                    className="light-blue"
                                />
                                <ErrorMessage
                                    name="next_of_kin_national_id"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="form-row">
                            <button
                                type="submit"
                                className="button primary"
                                disabled={loading}
                            >
                                {loading ? "Updating..." : "Update Tenant"}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>

            {/* Loading Backdrop */}
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </div>
    );
}